/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { getByIdAsync, getVariantsByDimensionValuesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

// tslint:disable-next-line: completed-docs
export class BuyboxGetDimensionVariantAvailabilityInput implements IActionInput {
    public channelId: number;
    public productId: number;
    constructor(productId: number, channelId: number) {
        this.channelId = channelId;
        this.productId = productId;
    }
    public getCacheKey = () => `VariantIDs-${this.productId}`;
    public getCacheObjectType = () => 'VariantIDs';
    public dataCacheType = (): CacheType => 'application';
}

export interface IBuyboxGetDimensionVariantAvailabilityActionData {
    variantID: number;
    sizeDimensionRecordID: number;
    colorDimensionRecordId: number;
    dimensionRecordID?: number;
    itemID: string;
    sizeValue: string;
    isAvailable: boolean;
    secondaryDimensionValue?: string; // can be color or height
}

const createInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput => {
    if (inputData && inputData.requestContext && inputData.requestContext.query && inputData.requestContext.query.productId) {
        return new BuyboxGetDimensionVariantAvailabilityInput(
            +inputData.requestContext.query.productId,
            +inputData.requestContext.apiSettings.channelId
        );
    } else if (inputData && inputData.requestContext && inputData.requestContext.urlTokens && inputData.requestContext.urlTokens.itemId) {
        return new BuyboxGetDimensionVariantAvailabilityInput(
            +inputData.requestContext.urlTokens.itemId,
            +inputData.requestContext.apiSettings.channelId
        );
    } else if (inputData && inputData.config && inputData.config.productId) {
        return new BuyboxGetDimensionVariantAvailabilityInput(inputData.config.productId, inputData.requestContext.apiSettings.channelId);
    } else {
        throw new Error('Unable to create SelectedVariantInput, no productId found on module config or query');
    }
};

export async function getAllVariantsAction(
    input: BuyboxGetDimensionVariantAvailabilityInput,
    ctx: IActionContext
): Promise<IBuyboxGetDimensionVariantAvailabilityActionData[]> {
    const fetchedVariants: IBuyboxGetDimensionVariantAvailabilityActionData[] = [];
    const productResponse = await getByIdAsync({ callerContext: ctx, queryResultSettings: { Paging: { Top: 1 } } }, input.productId, input.channelId);
    const baseProduct = Array.isArray(productResponse) ? productResponse[0] : productResponse;
    await getVariantsByDimensionValuesAsync(
        { callerContext: ctx, queryResultSettings: { Paging: { Top: 500 } } },
        baseProduct.MasterProductId || input.productId,
        input.channelId,
        []
    )
        .then(async (variants: SimpleProduct[]) => {
            // TODO: refactor code to use Hashmap
            // ex: https://codepen.io/asadalikanwal/pen/ZEpgmzd?editors=1111
            if (variants) {
                variants.forEach((variant) => {
                    const variantAvailabilityData: IBuyboxGetDimensionVariantAvailabilityActionData = { variantID: -1, sizeDimensionRecordID: -1, colorDimensionRecordId: -1, itemID: '', isAvailable: true, sizeValue: '' };
                    variant.Dimensions?.forEach((dimension) => {
                        if (dimension.DimensionTypeValue === 1) {
                            variantAvailabilityData.variantID = variant.RecordId;
                            variantAvailabilityData.colorDimensionRecordId = dimension.DimensionValue!.RecordId;
                            variantAvailabilityData.itemID = variant.ItemId!;
                            variantAvailabilityData.secondaryDimensionValue = dimension.DimensionValue?.Value;
                        } else if (dimension.DimensionTypeValue === 3) {
                            variantAvailabilityData.variantID = variant.RecordId;
                            variantAvailabilityData.sizeDimensionRecordID = dimension.DimensionValue!.RecordId;
                            variantAvailabilityData.sizeValue = dimension.DimensionValue!.Value!;
                        }
                    });
                    fetchedVariants.push(variantAvailabilityData);
                });
            }
            return fetchedVariants;
        })
        .catch(error => {
            ctx.telemetry.exception(error);
            throw new Error('[IBuyboxGetDimensionPricingActionData]Unable to retieve data');
        });
    return fetchedVariants;
}

export default createObservableDataAction({
    id: 'VariantIDs',
    action: <IAction<IBuyboxGetDimensionVariantAvailabilityActionData[]>>getAllVariantsAction,
    input: createInput
});
